import React, { useContext } from 'react';
import FileUpload from '../fileUpload/FileUpload';
import StatusContext from '../../context/StatusContext';
import style from './UploadDriveIMEIcsv.module.css';

/**
 * IMEIFileHandler
 * ---------------
 * This component acts as the controller around generic <FileUpload/>
 * and applies CSV-only validations BEFORE lifting the File object up.
 *
 * Key responsibilities:
 *   • Act as a "gatekeeper" for client-side CSV file validation
 *   • Show toast messages (success/error) via StatusContext
 *   • If valid, lift state up using setSelectedFile() from parent
 *   • Send "Upload" button click back to parent via onConfirmClick()
 *
 * This component does NOT:
 *   × Parse CSV rows
 *   × Call backend API
 *   × Display upload results
 */
const IMEIFileHandler = ({
  fetchState, // Current upload fetch state (e.g. LOADING / SUCCESS / ERROR) — passed into FileUpload to auto-disable UI if needed
  selectedFile, // The currently chosen CSV file, lifted from parent
  setSelectedFile, // Setter from parent to update currently selected file
  onReset, // Callback from parent to fully reset state and UI
  onConfirmClick // Callback to open confirmation modal / start upload
}) => {
  const { addErrorMessage, addSuccessMessage } = useContext(StatusContext);

  /** ---------- VALIDATION CONSTANTS ---------- */

  // Enforced max upload size (large enough to hold many IMEI rows)
  const MAX_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

  // Minimal MIME list — some browsers report text/plain even for CSV
  const allowedMime = ['text/csv', 'application/csv', 'text/plain'];

  // Enforce ".csv" as final extension (case-insensitive)
  const allowedExt = /\.csv$/i;

  /**
   * Handler triggered when <FileUpload/> fires onUpload(file)
   * Performs all validations and only accepts safe CSV File objects.
   */
  const onFilePicked = file => {
    // If user removes file from picker or cancels selection
    if (!file) {
      setSelectedFile(null);
      return;
    }

    /** --- Extension Validation --- */
    if (!allowedExt.test(file.name)) {
      addErrorMessage({ details: 'File must have .csv extension.' });
      return;
    }

    /** --- MIME Type Validation ---
     * We still allow text/plain but complain if clearly not CSV-type.
     * We DO NOT rely solely on MIME for security but for user feedback.
     */
    if (file.type && !allowedMime.includes(file.type)) {
      addErrorMessage({
        details: `Unexpected file MIME type: ${file.type}. Expected text/csv or application/csv.`
      });
      return;
    }

    /** --- Size Validation --- */
    if (file.size > MAX_SIZE_BYTES) {
      addErrorMessage({
        details: `File exceeds maximum size of 50 MB. (${(
          file.size /
          (1024 * 1024)
        ).toFixed(2)} MB)`
      });
      return;
    }

    /** --- All checks passed --- */
    setSelectedFile(file); // Lift clean file to parent
    addSuccessMessage({ message: `Selected file: ${file.name}` });
  };

  /**
   * Render wrapper around generic <FileUpload/>
   * FileUpload itself does not know about IMEI logic or CSV rules.
   */
  return (
    <FileUpload
      title="Upload IMEI–ICCID CSV"
      inputPlaceholderText="Click here or drag & drop a CSV file"
      inputSubtitleText="Only CSV files up to 50 MB are supported"
      /** FileUpload will call this with the File object on selection */
      onReset={onReset}
      onUpload={onFilePicked}
      /** "Upload File" triggers parent to open confirmation modal */
      actionBtnClick={onConfirmClick}
      actionBtnName="Upload File"
      className={style.file_upload}
      /** Passed down so FileUpload can disable UI during upload */
      fetchState={fetchState}
      /** Shows filename in FileUpload if exists */
      selectedFile={selectedFile}
      /** Business requirement: remove list toggle entirely */
      hideToggle={true}
      hideDownloadButton={true}
    />
  );
};

export default IMEIFileHandler;
